import { useEffect, useState } from "react";
import {
  takeNextThinkingMessage,
  thinkingMessages,
  type ThinkingMessage,
  type ThinkingMessageDeck,
} from "./thinking-messages";

const thinkingMessageStorageKey = "omerisbusy:thinking-message-deck";
let inMemoryDeck: ThinkingMessageDeck | undefined;

function loadThinkingMessageDeck(): ThinkingMessageDeck {
  if (inMemoryDeck) return inMemoryDeck;

  try {
    const storedDeck = window.sessionStorage.getItem(thinkingMessageStorageKey);
    if (storedDeck) {
      const parsedDeck = JSON.parse(storedDeck) as ThinkingMessageDeck;
      if (Array.isArray(parsedDeck.remaining)) {
        inMemoryDeck = parsedDeck;
        return inMemoryDeck;
      }
    }
  } catch {
    // Private browsing or storage restrictions should not prevent a loading state.
  }

  inMemoryDeck = { remaining: [] };
  return inMemoryDeck;
}

function pickNextThinkingMessage(): ThinkingMessage {
  const next = takeNextThinkingMessage(loadThinkingMessageDeck());
  inMemoryDeck = next.deck;

  try {
    window.sessionStorage.setItem(thinkingMessageStorageKey, JSON.stringify(next.deck));
  } catch {
    // Keep the in-memory randomized sequence when session storage is unavailable.
  }

  return next.message;
}

export function ThinkingStatus({ className = "", compact = false }: { className?: string; compact?: boolean }) {
  const [message, setMessage] = useState<ThinkingMessage>(() => compact ? thinkingMessages[0] : pickNextThinkingMessage());

  useEffect(() => {
    if (compact) return;

    const timer = window.setInterval(() => {
      setMessage(pickNextThinkingMessage());
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [compact]);

  return (
    <span className={`thinking-status ${compact ? "thinking-status--compact" : ""} ${className}`.trim()} role="status">
      <span className="sr-only">{compact ? "Loading" : "AI is working"}</span>
      <span className="thinking" aria-hidden="true"><i /><i /><i /></span>
      {!compact && <span className="thinking-status-message" aria-hidden="true">{message}</span>}
    </span>
  );
}
